// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { xSwiperStickyDots } from "@turnerBlueprint/App/Components/XngCarousel";
import { BannerCarousalStyles } from "@turnerBlueprint/App/Widgets/BannerCarousel/BannerCarousel";
import { LazyImageStyle } from "@turnerBlueprint/MobiusOverrides/LazyImage";
import { fontConfig } from "@turnerBlueprint/XDL/PreStyle/typography";
import { xScreen } from "@turnerBlueprint/XDL/Utilities";
import { css } from "styled-components";

BannerCarousalStyles.carouselWrapper = {
    container: {
        css: css`
            isolation: isolate;
            &&& .xng-pagination-container {
                margin-top: 0;
            }

            &&& .swiper-slide {
                margin-top: 0;
                margin-bottom: 0;
            }
            ${xScreen("lte", "md")} {
                && {
                    ${xSwiperStickyDots}
                }
            }
        `,
    },
};

BannerCarousalStyles.itemWrapper = {
    width: 12,
    css: css`
        padding: 0;
        ${xScreen("lte", "md")} {
            height: 100%;
        }
    `,
};

const containerFlex = `
    display: flex;
    width: 100%;
`;

BannerCarousalStyles.slideImageAndContentWrapper = {
    css: css`
        ${containerFlex}
        height: 384px;
        overflow: hidden;
        ${({ isReversed, backgroundColor, height }: any) => `
                height: ${height}px;
                flex-direction: ${isReversed ? "row-reverse" : "row"};
                background: ${backgroundColor};
            `}

        ${({ isClickable }: any) =>
            isClickable &&
            css`
                cursor: pointer;
            `}

        ${xScreen("lte", "md")} {
            flex-direction: column-reverse;
            height: 100%;
            justify-content: flex-end;
        }

        ${BLUEPRINT_NAME === "turnerScienceBlueprint" &&
        css`
            ${xScreen("gte", "xl")} {
                height: 524px;
            }
        `}
    `,
};

BannerCarousalStyles.slideContentWrapper = {
    css: css`
        ${containerFlex}
        flex-direction: column;
        padding: ${({ padding }: any) => padding || "32px 0 0 74px"};
        overflow: hidden;
        box-sizing: border-box;

        ${xScreen("lte", "md")} {
            padding: 16px;
            padding-bottom: 72px;
        }
    `,
};

BannerCarousalStyles.slideButton = {
    css: css`
        width: fit-content;
        margin-top: 4px;
    `,
};

BannerCarousalStyles.slideImageContainer = {
    css: css`
        ${containerFlex}
        justify-content: center;
        align-items: center;
        background: transparent;
        overflow: hidden;
        ${({ fullHeight, slideType }: any) => (fullHeight && slideType === "ContentAndImage" ? "flex: 0 0 678px;" : "")}
        &&& img {
            ${({ fullHeight }: any) =>
                fullHeight
                    ? css`
                          min-width: 678px;
                          height: 100%;
                      `
                    : css`
                          min-width: 400px;
                      `}
        }
        &&& picture {
            ${({ fullHeight }: any) => (fullHeight ? "height: 100%; width: 100%;" : "")}
        }
        ${xScreen("lte", "md")} {
            flex: 1;
            & > div {
                width: 100%;
            }
            &&& img {
                max-height: 368px;
                min-width: unset;
                width: 100%;
                object-fit: cover;
            }
        }

        ${({ fullHeight, slideType }: any) =>
            fullHeight &&
            slideType === "ImageOnly" &&
            css`
                ${LazyImageStyle} {
                    width: 100%;
                    img {
                        object-fit: cover;
                        width: 100%;
                    }
                }
            `}
    `,
};

BannerCarousalStyles.slideTitle = {
    variant: "h4",
    css: css`
        &&& {
            margin-bottom: 8px;
            ${({ color }: any) => `
            color: ${color};
        `}
        }
    `,
};

BannerCarousalStyles.slideSubTitle = {
    weight: fontConfig.weight.black,
    css: css`
        font-size: 38px;
        line-height: 46px;
        margin-bottom: 12px;
        &&& {
            ${({ color }: any) => `
            color: ${color};
        `}
        }

        ${xScreen("lte", "md")} {
            font-size: 24px;
            line-height: 28px;
        }
    `,
};

BannerCarousalStyles.contentText = {
    variant: "h3",
    forwardAs: "span",
    css: css`
        margin-bottom: 0;
        ul {
            padding: 0 0 0 20px;
            line-height: 26px;
        }
        li {
            line-height: 26px;
            font-size: 16px;
            font-weight: ${fontConfig.weight.regular};
            margin: 0; /* rid-off default margins */
            &:not(:last-of-type) {
                margin: 0 0 8px 0;
            }
        }
    `,
};
